<template>
  <div class="min-w-0 py-2" :class="compact ? 'px-2' : 'px-4'">
    <div class="mb-3 flex gap-2" :class="compact ? 'flex-col items-stretch' : 'items-center justify-between'">
      <div class="flex items-center gap-2">
        <span class="text-xs font-semibold uppercase tracking-wide text-slate-300">Sound Editor</span>
        <span v-if="modified" class="rounded bg-amber-400/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-300">Modified</span>
      </div>
      <button v-if="modified" type="button" class="rounded border border-slate-600 bg-slate-800 px-2 py-1 text-xs text-cyan-200 hover:bg-slate-700" @click="$emit('reset')">Reset to {{ preset.label }}</button>
    </div>

    <div class="grid min-w-0 grid-cols-1 gap-4">
      <section class="min-w-0">
        <div class="mb-2 flex items-center gap-3">
          <span class="text-xs font-semibold uppercase tracking-wide text-slate-400">Envelope</span>
          <div class="h-px flex-1 bg-slate-600/80"></div>
        </div>
        <div class="min-w-0">
          <SynthAdsrEditor :adsr="track.adsr" @update:adsr="$emit('update-sound', { adsr: $event })" />
        </div>
      </section>

      <section class="min-w-0">
        <div class="mb-2 flex items-center gap-3">
          <span class="text-xs font-semibold uppercase tracking-wide text-slate-400">Oscillators</span>
          <div class="h-px flex-1 bg-slate-600/80"></div>
        </div>
        <div class="grid min-w-0 grid-cols-1 gap-3" :class="compact ? '' : 'sm:grid-cols-2'">
          <div v-for="(layer, index) in track.layers" :key="index" class="min-w-0">
            <div v-if="index > 0" class="mb-3 h-px bg-slate-700/80"></div>
            <div class="mb-2 flex items-center justify-between text-xs font-medium text-slate-200">
              <span>Layer {{ index + 1 }}</span>
              <input type="checkbox" class="accent-cyan-400" :checked="layer.enabled" @change="updateLayer(index, { enabled: $event.target.checked })" />
            </div>
            <select class="mb-2 w-full rounded border border-slate-600 bg-slate-900 px-2 py-1 text-xs text-slate-100" :value="layer.waveform" @change="updateLayer(index, { waveform: $event.target.value })">
              <option v-for="wave in waveforms" :key="wave" :value="wave">{{ wave }}</option>
            </select>
            <SoundNumber label="Mix" unit="%" :value="layer.volumePercent" :min="0" :max="100" @change="updateLayer(index, { volumePercent: $event })" />
            <SoundNumber label="Detune" unit="ct" :value="layer.detuneCents" :min="-1200" :max="1200" @change="updateLayer(index, { detuneCents: $event })" />
            <SoundNumber v-if="layer.waveform === 'square'" label="Pulse" unit="%" :value="layer.pulseWidthPercent" :min="0" :max="100" @change="updateLayer(index, { pulseWidthPercent: $event })" />
          </div>
        </div>
      </section>

      <section class="min-w-0">
        <div class="mb-2 flex items-center gap-3">
          <span class="text-xs font-semibold uppercase tracking-wide text-slate-400">Modulation</span>
          <div class="h-px flex-1 bg-slate-600/80"></div>
        </div>
        <label class="mb-2 flex items-center justify-between text-xs text-slate-300">
          Vibrato
          <input type="checkbox" class="accent-cyan-400" :checked="track.vibrato.enabled" @change="updateVibrato({ enabled: $event.target.checked })" />
        </label>
        <SoundNumber label="Rate" unit="Hz" :value="track.vibrato.rateHz" :min="0" :disabled="!track.vibrato.enabled" @change="updateVibrato({ rateHz: $event })" />
        <SoundNumber label="Depth" unit="Hz" :value="track.vibrato.depthHz" :min="0" :disabled="!track.vibrato.enabled" @change="updateVibrato({ depthHz: $event })" />
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, h } from 'vue'
import { SYNTH_WAVEFORMS } from '../../stores/synthModel.js'
import { isTrackSoundModified, resolveInstrumentPreset } from '../../stores/synthInstruments.js'
import SynthAdsrEditor from './SynthAdsrEditor.vue'

const props = defineProps({
  track: { type: Object, required: true },
  compact: { type: Boolean, default: false },
})
const emit = defineEmits(['update-sound', 'reset'])
const waveforms = SYNTH_WAVEFORMS
const preset = computed(() => resolveInstrumentPreset(props.track.instrument))
const modified = computed(() => isTrackSoundModified(props.track))

function updateLayer(index, patch) {
  const layers = props.track.layers.map((layer, layerIndex) => layerIndex === index ? { ...layer, ...patch } : layer)
  emit('update-sound', { layers })
}

function updateVibrato(patch) {
  emit('update-sound', { vibrato: { ...props.track.vibrato, ...patch } })
}

const SoundNumber = {
  props: {
    label: String, unit: String, value: Number, min: Number, max: Number, disabled: Boolean,
  },
  emits: ['change'],
  setup(numberProps, { emit: emitNumber }) {
    return () => h('label', { class: 'mb-1 grid grid-cols-[1fr_76px] items-center gap-2 text-[11px] text-slate-400' }, [
      `${numberProps.label}${numberProps.unit ? ` (${numberProps.unit})` : ''}`,
      h('input', {
        class: 'w-full rounded border border-slate-600 bg-slate-900 px-1.5 py-1 text-xs text-slate-100 disabled:opacity-40',
        type: 'number', min: numberProps.min, max: numberProps.max, step: 0.01,
        value: numberProps.value, disabled: numberProps.disabled,
        onInput: event => emitNumber('change', Number(event.target.value)),
      }),
    ])
  },
}
</script>
